import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ListGroup, Badge, ButtonGroup, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { 
  faHospital, 
  faUsers, 
  faDollarSign, 
  faUserPlus,
  faArrowUp,
  faArrowDown,
  faCalendarCheck,
  faCalendarTimes,
  faFileInvoiceDollar,
  faMoneyBillWave,
  // --- NOVOS ÍCONES ---
  faClipboardList,
  faTrophy,
  faCreditCard,
  faBullhorn,
  faTags,
  faReceipt, // Ícone para o novo card
  // --- ÍCONES PARA AS NOVAS MÉTRICAS ---
  faExclamationTriangle,
  faPuzzlePiece,
  faStethoscope,
  faPercent,
  faMinusCircle,
  faBell,
  faDatabase,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Top5ListCard from '../components/dashboard/Top5ListCard'; // Importar o novo componente
import CrescimentoPlataformaChart from '../components/dashboard/CrescimentoPlataformaChart';

// Componente de Card de Estatísticas (pode ficar em admin/components/dashboard/StatsCard.js)
const StatsCard = ({ title, value, icon, variant }) => (
  <Card className={`bg-${variant} text-white h-100`}>
    <Card.Body>
      <Row>
        <Col xs={3} className="d-flex align-items-center justify-content-center">
          <FontAwesomeIcon icon={icon} size="3x" />
        </Col>
        <Col xs={9} className="text-end">
          <div className="display-4">{value}</div>
          <div>{title}</div>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const AdminDashboardPage = () => {
  const [chartTimeRange, setChartTimeRange] = useState('6m'); // '1m', '6m', '1y'
  const [chartMetric, setChartMetric] = useState('clinicas'); // 'clinicas', 'receita', 'usuarios'
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // --- NOVOS ESTADOS PARA O GRÁFICO ---
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [chartError, setChartError] = useState(null);
  const [error, setError] = useState(null);
  // --- NOVOS ESTADOS PARA DADOS FINANCEIROS ---
  const [financialData, setFinancialData] = useState(null);
  const [isFinancialLoading, setIsFinancialLoading] = useState(true);
  const [financialError, setFinancialError] = useState(null);
  // --- NOVOS ESTADOS PARA ANÁLISE DE CLÍNICAS ---
  const [clinicAnalysisData, setClinicAnalysisData] = useState(null);
  const [isClinicAnalysisLoading, setIsClinicAnalysisLoading] = useState(true);
  const [clinicAnalysisError, setClinicAnalysisError] = useState(null);
  // --- NOVOS ESTADOS PARA ENGAJAMENTO ---
  const [engagementData, setEngagementData] = useState(null);
  const [isEngagementLoading, setIsEngagementLoading] = useState(true);
  const [engagementError, setEngagementError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/admin/dashboard/stats');
        setStats(response.data);
        setError(null);
      } catch (err) {
        setError('Não foi possível carregar as estatísticas do dashboard.');
        console.error("Erro ao buscar estatísticas do admin:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);
  
  // --- NOVO useEffect PARA BUSCAR DADOS FINANCEIROS ---
  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setIsFinancialLoading(true);
        const response = await axios.get('/api/admin/dashboard/financial-overview');
        setFinancialData(response.data);
        setFinancialError(null);
      } catch (err) {
        setFinancialError('Não foi possível carregar os dados financeiros.');
        console.error("Erro ao buscar dados financeiros do admin:", err);
      } finally {
        setIsFinancialLoading(false);
      }
    };
    fetchFinancialData();
  }, []);

  // --- NOVO useEffect PARA BUSCAR DADOS DE ANÁLISE DE CLÍNICAS ---
  useEffect(() => {
    const fetchClinicAnalysisData = async () => {
      try {
        setIsClinicAnalysisLoading(true);
        const response = await axios.get('/api/admin/dashboard/clinic-analysis');
        setClinicAnalysisData(response.data);
        setClinicAnalysisError(null);
      } catch (err) {
        setClinicAnalysisError('Não foi possível carregar os dados de análise das clínicas.');
        console.error("Erro ao buscar dados de análise das clínicas:", err);
      } finally {
        setIsClinicAnalysisLoading(false);
      }
    };
    fetchClinicAnalysisData();
  }, []);

  // --- NOVO useEffect PARA BUSCAR DADOS DE ENGAJAMENTO ---
  useEffect(() => {
    const fetchEngagementData = async () => {
      try {
        setIsEngagementLoading(true);
        const response = await axios.get('/api/admin/dashboard/engagement-analysis');
        setEngagementData(response.data);
        setEngagementError(null);
      } catch (err) {
        setEngagementError('Não foi possível carregar os dados de engajamento.');
        console.error("Erro ao buscar dados de engajamento:", err);
      } finally {
        setIsEngagementLoading(false);
      }
    };
    fetchEngagementData();
  }, []);
  // --- NOVO useEffect PARA BUSCAR DADOS DO GRÁFICO ---
  useEffect(() => {
    const chartMetricConfig = {
      clinicas: { label: 'Total de Clínicas', backgroundColor: 'rgba(75, 192, 192, 0.2)', borderColor: 'rgba(75, 192, 192, 1)' },
      receita: { label: 'Receita Mensal (R$)', backgroundColor: 'rgba(54, 162, 235, 0.2)', borderColor: 'rgba(54, 162, 235, 1)' },
      usuarios: { label: 'Total de Profissionais', backgroundColor: 'rgba(255, 159, 64, 0.2)', borderColor: 'rgba(255, 159, 64, 1)' },
    };

    const fetchChartData = async () => {
      // Otimização: Garante que a chamada só seja feita se os filtros estiverem definidos.
      if (!chartMetric || !chartTimeRange) {
        setChartError("Métrica ou período do gráfico não selecionado.");
        return;
      }

      setIsChartLoading(true);
      setChartError(null);
      try {
        const response = await axios.get('/api/admin/dashboard/evolution-chart', {
          params: { metric: chartMetric, range: chartTimeRange }
        });
        const config = chartMetricConfig[chartMetric];
        setChartData({
          labels: response.data.labels,
          datasets: [{
            label: config.label,
            data: response.data.data,
            fill: true,
            backgroundColor: config.backgroundColor,
            borderColor: config.borderColor,
            tension: 0.3,
          }],
        });
      } catch (err) {
        setChartError('Não foi possível carregar os dados do gráfico.');
        console.error("Erro ao buscar dados do gráfico de evolução:", err);
      } finally {
        setIsChartLoading(false);
      }
    };

    fetchChartData();
  }, [chartMetric, chartTimeRange]);

  // --- DADOS MOCK ---
  // O objeto mockStats será substituído pelo estado 'stats' que vem da API.

  // --- DADOS E OPÇÕES PARA O GRÁFICO ---
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          // Formata o eixo Y dinamicamente
          callback: function(value) {
            if (chartMetric === 'receita') {
              return 'R$ ' + value.toLocaleString('pt-BR');
            }
            // Garante que os ticks para clínicas e usuários sejam números inteiros
            if (Number.isInteger(value)) {
              return value;
            }
          },
        },
      },
    },
  };
  return (
    <Container fluid>
      {/* --- NOVA SEÇÃO: GESTÃO DE DADOS E LIMPEZA --- */}
      
      <h1 className="mb-3">Dashboard Administrativo</h1>

      {isLoading && (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando estatísticas...</span>
          </Spinner>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-4">
        <Col md={6} xl={3}>
          <StatsCard title="Espaço usado em Disco" value={stats?.espacoEmDisco || '...'} icon={faDatabase} variant="dark" />
        </Col>
        <Col md={6} xl={2}>
          <StatsCard title="Clínicas (Mês)" value={`+${stats?.novosCadastrosMes || 0}`} icon={faUserPlus} variant="warning" />
        </Col>
        <Col md={6} xl={2}>
          <StatsCard title="Clínicas Ativas" value={stats?.totalEntidades || 0} icon={faHospital} variant="primary" />
        </Col>
        <Col md={6} xl={2}>
          <StatsCard title="Profissionais" value={stats?.totalUsuarios || 0} icon={faUsers} variant="info" />
        </Col>
        <Col md={6} xl={3}>
          <StatsCard title="Taxa de Agendamentos" value={`${stats?.taxaAgendamentos || 0}%`} icon={faPercent} variant="secondary" />
        </Col>
        
      </Row>

      {/* --- NOVA SEÇÃO: MÉTRICAS FINANCEIRAS E DE CRESCIMENTO (SAAS) --- */}
      <h3 className="mt-5 mb-3">Métricas de Negócio (SaaS)</h3>
      <Row className="g-4">
        
        <Col md={6} xl={3}>
          <StatsCard title="Sem Login(7 Dias)" value={`${stats?.churnRate || 0}%`} icon={faMinusCircle} variant="danger" />
        </Col>
        <Col md={6} xl={3}>
          <StatsCard title="Receita Mensal (MRR)" value={`R$ ${stats?.mrr || 0}`} icon={faDollarSign} variant="success" />
        </Col>
        <Col md={6} xl={3}>
          <StatsCard title="ARPA (Receita/Clínica)" value={`R$ ${stats?.ltv || 0}`} icon={faDollarSign} variant="success" />
        </Col>
        <Col md={6} xl={3}>
          <StatsCard title="Receita Anual (ARR)" value={`R$ ${stats?.arr || 0}`} icon={faDollarSign} variant="success" />
        </Col>
      </Row>



      {/* --- GRÁFICO DE CRESCIMENTO --- */}
      <Row className="mt-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-2 mb-md-0">Evolução da Plataforma</h6>
              <div className="d-flex flex-wrap">
                <ButtonGroup size="sm" className="me-2 mb-2 mb-md-0">
                  <Button variant={chartMetric === 'clinicas' ? 'primary' : 'outline-secondary'} onClick={() => setChartMetric('clinicas')}>Clínicas</Button>
                  <Button variant={chartMetric === 'receita' ? 'primary' : 'outline-secondary'} onClick={() => setChartMetric('receita')}>Receita</Button>
                  <Button variant={chartMetric === 'usuarios' ? 'primary' : 'outline-secondary'} onClick={() => setChartMetric('usuarios')}>Usuários</Button>
                </ButtonGroup>
                <ButtonGroup size="sm" className="mb-2 mb-md-0">
                  <Button variant={chartTimeRange === '1m' ? 'primary' : 'outline-secondary'} onClick={() => setChartTimeRange('1m')}>Mês</Button>
                  <Button variant={chartTimeRange === '6m' ? 'primary' : 'outline-secondary'} onClick={() => setChartTimeRange('6m')}>6 Meses</Button>
                  <Button variant={chartTimeRange === '1y' ? 'primary' : 'outline-secondary'} onClick={() => setChartTimeRange('1y')}>Ano</Button>
                </ButtonGroup>
              </div>
            </Card.Header>
            <Card.Body>
              {isChartLoading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando gráfico...</span>
                  </Spinner>
                </div>
              ) : chartError ? (
                <Alert variant="danger">{chartError}</Alert>
              ) : (
                <div style={{ height: '300px' }}>
                  <CrescimentoPlataformaChart data={chartData} options={chartOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- NOVAS SEÇÕES DE FATURAMENTO E PLANOS --- */}
      <h3 className="mt-5 mb-3">Visão Geral Financeira</h3>
      <Row className="g-4">
        {isFinancialLoading ? (
          <div className="text-center w-100"><Spinner animation="border" /></div>
        ) : financialError ? (
          <Alert variant="danger" className="w-100">{financialError}</Alert>
        ) : (
          <>
            <Col md={6} xl={3}>
              <Card className="h-100 shadow-sm">
                <Card.Header as="h6" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />
                  Clínicas por Plano
                </Card.Header>
                <ListGroup variant="flush">
                  {financialData?.planStats && Object.entries(financialData.planStats).map(([plano, qtd]) => (
                    <ListGroup.Item key={plano} className="d-flex justify-content-between align-items-center small text-capitalize">
                      <span>{plano}</span><Badge bg="secondary" pill>{qtd}</Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Maior Faturamento (Mês)" icon={faTrophy} items={financialData?.top5Revenue} isCurrency />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Maior Ticket Médio" icon={faReceipt} items={financialData?.top5TicketMedio} isCurrency />
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100 shadow-sm">
                <Card.Header as="h6" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />
                  Faturamento por Pagamento
                </Card.Header>
                <ListGroup variant="flush">
                  {financialData?.avgRevenueByPayment.map((item, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center small">
                      <span>{item.nome}</span>
                      <span className="fw-bold">R$ {parseFloat(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>
            
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 4 - Maiores Compras de Créditos" icon={faArrowUp} items={financialData?.top4MaioresComprasCreditos} isCurrency />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 4 - Menores Compras de Créditos" icon={faArrowDown} items={financialData?.top4MenoresComprasCreditos} isCurrency />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Clínicas com Contas em Atraso" icon={faBell} items={financialData?.contasAtraso} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Mais investem em Marketing" icon={faBullhorn} items={financialData?.top5Marketing} isCurrency />
            </Col>
          </>
        )}
      </Row>

      {/* --- NOVAS SEÇÕES TOP 5 --- */}
      <h3 className="mt-5 mb-3">Análise de Clínicas</h3>
      <Row className="g-4">
        {isClinicAnalysisLoading ? (
          <div className="text-center w-100"><Spinner animation="border" /></div>
        ) : clinicAnalysisError ? (
          <Alert variant="danger" className="w-100">{clinicAnalysisError}</Alert>
        ) : (
          <>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Mais Pacientes" icon={faArrowUp} items={clinicAnalysisData?.maisPacientes} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Menos Pacientes" icon={faArrowDown} items={clinicAnalysisData?.menosPacientes} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Mais Agendamentos (30d)" icon={faCalendarCheck} items={clinicAnalysisData?.maisAgendamentos} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Menos Agendamentos (30d)" icon={faCalendarTimes} items={clinicAnalysisData?.menosAgendamentos} />
            </Col>
          </>
        )}
      </Row>
      
      <h3 className="mt-5 mb-3">Engajamento com a Plataforma</h3>
      <Row className="g-4">
        {isEngagementLoading ? (
          <div className="text-center w-100"><Spinner animation="border" /></div>
        ) : engagementError ? (
          <Alert variant="danger" className="w-100">{engagementError}</Alert>
        ) : (
          <>
            <Col md={6} xl={3}>
              <Top5ListCard title="Clínicas sem Folha de Pagamento (Mês Ant.)" icon={faFileInvoiceDollar} items={engagementData?.semFolhaPagamento} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Clínicas sem Despesas (30d)" icon={faMoneyBillWave} items={engagementData?.semDespesas} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Top 5 - Menos utilizam Tags" icon={faTags} items={engagementData?.top5FewestTags} />
            </Col>
            <Col md={6} xl={3}>
              <Top5ListCard title="Clínicas com Baixa Atividade" icon={faExclamationTriangle} items={engagementData?.baixaAtividade} />
            </Col>
            <Col md={6} xl={4}>
              <Card className="h-100 shadow-sm">
                <Card.Header as="h6" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faPuzzlePiece} className="me-2 text-primary" />
                  Adoção de Funcionalidades
                </Card.Header>
                <ListGroup variant="flush">
                  {engagementData?.usoFuncionalidades.map((item, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center small">
                      <span>{item.nome}</span>
                      <Badge bg="primary" pill>{item.valor}</Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>
            <Col md={6} xl={4}>
              <Top5ListCard title="Top 5 - Serviços Mais Usados" icon={faStethoscope} items={engagementData?.servicosMaisUsados} />
            </Col>
            <Col md={6} xl={4}>
              <Top5ListCard title="Clínicas com Dados Incompletos" icon={faEdit} items={engagementData?.dadosIncompletos} />
            </Col>
          </>
        )}
      </Row>

      

    </Container>
  );
};

export default AdminDashboardPage;
